import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  makeStyles, 
  FormHelperText,
  InputAdornment
} from "@material-ui/core";
import React, { useRef, useState, useEffect } from "react";
import ErrorIcon from '@material-ui/icons/Error';
import DOMPurify from 'dompurify';

/**
 * Sanitizes SVG content to remove potentially malicious code
 * @param svgContent The SVG content as a string
 * @returns Sanitized SVG content
 */
export const sanitizeSvgContent = (svgContent: string): string => {
  // Configure DOMPurify specifically for SVG content
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Remove any JavaScript events (attributes starting with "on")
    Array.from(node.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        node.removeAttribute(attr.name);
      }
    });
    
    // Remove any href attributes that contain javascript
    if (node.hasAttribute('href')) {
      const href = node.getAttribute('href') || '';
      if (href.toLowerCase().includes('javascript:')) {
        node.removeAttribute('href');
      }
    }
  });

  // Apply sanitization to the SVG content with minimal configuration
  // Just focus on removing scripts and dangerous attributes
  return DOMPurify.sanitize(svgContent, {
    USE_PROFILES: { svg: true },
    FORBID_TAGS: ['script'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick']
  });
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  uploadButton: {
    marginRight: theme.spacing(1),
    height: 36,
    marginBottom: 3,
    position: 'absolute',
    right: 0,
    top: '28px',
    transform: 'translateY(-50%)',
    zIndex: 1,
    borderRadius: '4px',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  inputContainer: {
    width: '100%',
    position: 'relative',
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
  },
  input: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-error': {
        '& fieldset': {
          borderColor: theme.palette.error.main,
        },
      },
    },
  },
  errorIcon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(0.5),
  },
  helperText: {
    marginLeft: theme.spacing(1.5),
  }
}));

interface IImageFileUploadProps {
  label?: string | JSX.Element;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  error?: boolean;
  helperText?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onFileChange?: (file: File) => void;
}

export const ImageFileUpload = ({
  label,
  onChange,
  accept = "image/svg+xml",
  error = false,
  helperText,
  value = "",
  required = false,
  disabled = false,
  placeholder = "Upload SVG logo file",
  className,
  onFileChange,
  ...rest
}: IImageFileUploadProps) => { 
    const classes = useStyles();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [internalError, setInternalError] = useState<boolean>(error);
    const [internalValue, setInternalValue] = useState<string>(value);
    
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    useEffect(() => {
      setInternalError(error);
    }, [error]);
    
    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };
    
    const validateFile = (file: File): boolean => {
      // Check if file is SVG
      if (accept.includes('svg') && !file.type.includes('svg')) {
        setFileError('Only SVG files are supported');
        return false;
      }
      
      // Check file size (limit to 1MB)
      if (file.size > 1024 * 1024) {
        setFileError('File size should be less than 1MB');
        return false;
      }
      
      setFileError(null);
      return true;
    };
    
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        if (validateFile(file)) {
          // Update display value with file name
          const fileNameEvent = {
            target: {
              value: file.name
            }
          } as React.ChangeEvent<HTMLInputElement>;
          
          setInternalValue(file.name);
          onChange(fileNameEvent);
          
          // For SVG files, apply sanitization
          if (file.type.includes('svg')) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const svgContent = e.target?.result as string;
              if (svgContent) {
                // Sanitize the SVG content
                const sanitizedSvg = sanitizeSvgContent(svgContent);
                
                // Create a new sanitized Blob/File to pass to the callback
                const sanitizedBlob = new Blob([sanitizedSvg], { type: 'image/svg+xml' });
                const sanitizedFile = new File([sanitizedBlob], file.name, { type: 'image/svg+xml' });
                
                // Call the onFileChange callback with the sanitized file
                if (onFileChange) {
                  onFileChange(sanitizedFile);
                }
              }
            };
            
            // Read the file as text
            reader.readAsText(file);
          } else {
            // For non-SVG files, just pass the original file
            if (onFileChange) {
              onFileChange(file);
            }
          }
          
          setInternalError(false);
        } else {
          setInternalError(true);
        }
      }
    };
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setInternalValue(inputValue);
      
      // Validate URL if it looks like a URL
      if (inputValue.startsWith('http') && !inputValue.match(/^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i)) {
        setFileError('Please enter a valid URL');
        setInternalError(true);
      } else {
        setFileError(null);
        setInternalError(false);
      }
      
      onChange(event);
    };
    
    return (
      <Box className={`${classes.root} ${className}`}>
        <Box width="100%" className="image-upload-holder">
          <Box className={classes.inputContainer}>
            <TextField
              label={label}
              variant="outlined"
              fullWidth
              placeholder={placeholder}
              value={internalValue}
              onChange={handleInputChange}
              required={required}
              disabled={disabled}
              error={internalError}
              className={classes.input}
              InputProps={{
                endAdornment: internalError ? (
                  <InputAdornment position="end">
                    <ErrorIcon className={classes.errorIcon} />
                  </InputAdornment>
                ) : null,
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={accept}
              onChange={handleFileChange}
              disabled={disabled}
            />
            <Button 
              variant="outlined"
              color="primary"
              onClick={handleUploadClick}
              disabled={disabled}
              className={classes.uploadButton}
              size="small"
            >
              Upload
            </Button>
            {fileError && (
              <FormHelperText className={classes.error}>
                {fileError}
              </FormHelperText>
            )}
            {!fileError && helperText && (
              <FormHelperText className={classes.helperText}>
                {helperText}
              </FormHelperText>
            )}
            
          </Box>
        </Box>
      </Box>
    );
};
